import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PeriodSelector } from '../components/PeriodSelector';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Upload, 
  Receipt, 
  CheckCircle2, 
  AlertCircle,
  Pencil,
  Check,
  X,
  FileImage,
  Eye,
  Loader2,
  Trash2,
  CheckCheck,
  Calendar,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Clock,
  Sparkles,
  FileSpreadsheet,
  MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import FixedCostsTab from '../components/FixedCostsTab';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const RECURRENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annually', label: 'Annually' },
];

const ExpensesPage = () => {
  const { currentLocation, api } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingExpense, setPendingExpense] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [payrollResult, setPayrollResult] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [deleteExpense, setDeleteExpense] = useState(null);
  const [approving, setApproving] = useState(null);
  const [upcomingExpenses, setUpcomingExpenses] = useState([]);
  const [upcomingOpen, setUpcomingOpen] = useState(false);
  const [obligations, setObligations] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState('all'); // all, paid, unpaid
  const [partialPaymentDialog, setPartialPaymentDialog] = useState(null);
  const [partialPaymentForm, setPartialPaymentForm] = useState({ amount: '', payment_date: '', notes: '' });
  const [paymentHistoryDialog, setPaymentHistoryDialog] = useState(null);
  const fileInputRef = useRef(null);
  const payrollInputRef = useRef(null);
  const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(new Date()));
  const [periodPreset, setPeriodPreset] = useState('this_month');
  
  // Bot uploads state
  const [botUploads, setBotUploads] = useState([]);
  const [botUploadsOpen, setBotUploadsOpen] = useState(false);
  const [processingUpload, setProcessingUpload] = useState(null);
  const [processForm, setProcessForm] = useState({ vendor: '', amount: '', date: '', category: 'cogs' });


  useEffect(() => {
    if (currentLocation) {
      fetchExpenses();
      fetchUpcomingExpenses();
      fetchObligations();
      fetchBotUploads();
    }
  }, [currentLocation, periodStart, periodEnd]);

  // Auto-refresh expenses every 30 seconds to catch Telegram bot entries
  useEffect(() => {
    if (!currentLocation) return;
    
    const interval = setInterval(() => {
      // Silent refresh - don't show loading spinner
      api.get('/expenses', { params: {
        location_id: currentLocation.id,
        start_date: format(periodStart, 'yyyy-MM-dd'),
        end_date: format(periodEnd, 'yyyy-MM-dd'),
      }}).then(res => {
        setExpenses(prev => {
          // Only update if there are changes (different count or IDs)
          if (res.data.length !== prev.length) {
            return res.data;
          }
          const newIds = new Set(res.data.map(e => e.id));
          const oldIds = new Set(prev.map(e => e.id));
          const hasChanges = res.data.some(e => !oldIds.has(e.id)) || prev.some(e => !newIds.has(e.id));
          return hasChanges ? res.data : prev;
        });
      }).catch(() => {}); // Silent fail on auto-refresh
      
      // Also refresh obligations
      api.get('/expenses/obligations', { params: { location_id: currentLocation.id } })
        .then(res => setObligations(res.data))
        .catch(() => {});
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [currentLocation, periodStart, periodEnd]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/expenses', { params: {
        location_id: currentLocation.id,
        start_date: format(periodStart, 'yyyy-MM-dd'),
        end_date: format(periodEnd, 'yyyy-MM-dd'),
      }});
      setExpenses(res.data);
    } catch (err) {
      console.error('Error fetching expenses:', err);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchObligations = async () => {
    try {
      const res = await api.get('/expenses/obligations', { params: { location_id: currentLocation.id } });
      setObligations(res.data);
    } catch (err) {
      console.error('Error fetching obligations:', err);
    }
  };

  const fetchUpcomingExpenses = async () => {
    try {
      const res = await api.get('/expenses/upcoming', { params: { location_id: currentLocation.id, limit: 10 } });
      setUpcomingExpenses(res.data);
    } catch (err) {
      console.error('Error fetching upcoming expenses:', err);
    }
  };

  const fetchBotUploads = async () => {
    try {
      const res = await api.get('/bot-uploads', { params: { location_id: currentLocation.id, status: 'pending' } });
      setBotUploads(res.data || []);
    } catch (err) {
      console.error('Error fetching bot uploads:', err);
    }
  };

  const handleProcessBotUpload = async (upload) => {
    setProcessingUpload(upload);
    setProcessForm({
      vendor: upload.partial_ocr_data?.supplier_name || '',
      amount: upload.partial_ocr_data?.total || '',
      date: upload.partial_ocr_data?.invoice_date || format(new Date(), 'yyyy-MM-dd'),
      category: 'cogs'
    });
  };

  const submitProcessBotUpload = async () => {
    if (!processForm.vendor || !processForm.amount) {
      toast.error('Please fill in vendor and amount');
      return;
    }
    try {
      await api.post(`/bot-uploads/${processingUpload.id}/process`, {
        vendor: processForm.vendor,
        amount: parseFloat(processForm.amount),
        date: processForm.date,
        category: processForm.category,
        is_paid: false
      });
      toast.success('Receipt processed into expense');
      setProcessingUpload(null);
      fetchBotUploads();
      fetchExpenses();
    } catch (err) {
      console.error('Error processing upload:', err);
      toast.error('Failed to process upload');
    }
  };

  const rejectBotUpload = async (uploadId) => {
    try {
      await api.delete(`/bot-uploads/${uploadId}`);
      toast.success('Upload rejected');
      fetchBotUploads();
    } catch (err) {
      toast.error('Failed to reject upload');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image (PNG, JPG) or PDF');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location_id', currentLocation.id);

    try {
      const res = await api.post('/expenses/upload-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.needs_confirmation) {
        setPendingExpense({
          id: res.data.expense_id,
          file_id: res.data.file_id,
          ...res.data.extraction,
          filename: file.name
        });
        
        // Fetch the receipt image for preview
        try {
          setLoadingImage(true);
          const imgRes = await api.get(`/files/${res.data.file_id}/image`);
          setReceiptImage(imgRes.data.data_url);
        } catch (imgErr) {
          console.error('Could not load receipt image:', imgErr);
        } finally {
          setLoadingImage(false);
        }
      }
      
      toast.success('Receipt uploaded and processed');
      fetchExpenses();
    } catch (err) {
      console.error('Error uploading receipt:', err);
      toast.error('Failed to upload receipt');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePayrollUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location_id', currentLocation.id);

    try {
      const res = await api.post('/expenses/upload-payroll', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPayrollResult(res.data);
      toast.success('Payroll data imported successfully');
      fetchExpenses();
    } catch (err) {
      console.error('Error uploading payroll:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to upload payroll';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (payrollInputRef.current) {
        payrollInputRef.current.value = '';
      }
    }
  };

  const handleConfirmExpense = async () => {
    if (!pendingExpense) return;

    try {
      await api.put(`/expenses/${pendingExpense.id}/confirm`, {
        vendor: pendingExpense.vendor,
        date: pendingExpense.date,
        amount: pendingExpense.amount,
        category: pendingExpense.suggested_category || 'operating',
        is_recurring: pendingExpense.is_recurring || false,
        recurrence_frequency: pendingExpense.is_recurring ? pendingExpense.recurrence_frequency : null,
        recurrence_end_date: pendingExpense.is_recurring ? pendingExpense.recurrence_end_date : null,
        due_date: pendingExpense.due_date || null,
        is_personal: (pendingExpense.suggested_category || 'operating') === 'personal'
      });
      toast.success('Expense confirmed');
      setPendingExpense(null);
      setReceiptImage(null);
      fetchExpenses();
      fetchUpcomingExpenses();
      fetchObligations();
    } catch (err) {
      console.error('Error confirming expense:', err);
      toast.error('Failed to confirm expense');
    }
  };

  const handleViewReceipt = async (expenseId) => {
    try {
      setLoadingImage(true);
      const res = await api.get(`/expenses/${expenseId}/receipt`);
      setViewingReceipt({
        expenseId,
        ...res.data
      });
    } catch (err) {
      console.error('Error loading receipt:', err);
      if (err.response?.status === 404) {
        toast.error('No receipt attached to this expense');
      } else {
        toast.error('Failed to load receipt');
      }
    } finally {
      setLoadingImage(false);
    }
  };

  const handleTogglePayment = async (expense) => {
    try {
      const newStatus = !expense.is_paid;
      await api.put(`/expenses/${expense.id}/payment`, {
        is_paid: newStatus,
        paid_date: newStatus ? new Date().toISOString().split('T')[0] : null
      });
      toast.success(newStatus ? 'Marked as paid' : 'Marked as unpaid');
      fetchExpenses();
      fetchObligations();
    } catch (err) {
      console.error('Error updating payment status:', err);
      toast.error('Failed to update payment status');
    }
  };

  const openPartialPayment = (expense) => {
    setPartialPaymentDialog(expense);
    const remaining = expense.amount - (expense.amount_paid || 0);
    setPartialPaymentForm({
      amount: remaining.toFixed(2),
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const handlePartialPayment = async () => {
    if (!partialPaymentDialog || !partialPaymentForm.amount) return;
    
    try {
      await api.post(`/expenses/${partialPaymentDialog.id}/partial-payment`, {
        amount: parseFloat(partialPaymentForm.amount),
        payment_date: partialPaymentForm.payment_date,
        notes: partialPaymentForm.notes || null
      });
      toast.success(`Payment of £${parseFloat(partialPaymentForm.amount).toFixed(2)} recorded`);
      setPartialPaymentDialog(null);
      fetchExpenses();
      fetchObligations();
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentHistory = (expense) => {
    setPaymentHistoryDialog(expense);
  };

  const handleDeleteExpensePayment = async (expenseId, paymentId) => {
    try {
      await api.delete(`/expenses/${expenseId}/payments/${paymentId}`);
      toast.success('Payment deleted');
      // Refresh the expense to get updated payment list
      const res = await api.get('/expenses', { params: { location_id: currentLocation.id } });
      setExpenses(res.data);
      // Update the dialog with new data
      const updatedExpense = res.data.find(e => e.id === expenseId);
      if (updatedExpense) {
        setPaymentHistoryDialog(updatedExpense);
      }
      fetchObligations();
    } catch (err) {
      console.error('Error deleting payment:', err);
      toast.error('Failed to delete payment');
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense.id);
    setEditForm({
      vendor: expense.vendor,
      date: expense.date,
      amount: expense.amount,
      category: expense.category,
      is_recurring: expense.is_recurring || false,
      recurrence_frequency: expense.recurrence_frequency || '',
      recurrence_end_date: expense.recurrence_end_date || '',
      due_date: expense.due_date || '',
      is_paid: expense.is_paid || false
    });
  };

  const handleSaveEdit = async (expenseId) => {
    try {
      const updateData = {
        ...editForm,
        recurrence_frequency: editForm.is_recurring ? editForm.recurrence_frequency : null,
        recurrence_end_date: editForm.is_recurring ? editForm.recurrence_end_date : null,
        due_date: editForm.due_date || null
      };
      await api.put(`/expenses/${expenseId}/confirm`, updateData);
      toast.success('Expense updated');
      setEditingExpense(null);
      fetchExpenses();
      fetchUpcomingExpenses();
      fetchObligations();
    } catch (err) {
      console.error('Error updating expense:', err);
      toast.error('Failed to update expense');
    }
  };

  // Handle clicking on an upcoming payment - scroll to it and open for editing
  const handleUpcomingPaymentClick = async (obligationId) => {
    // First check if expense is in current filtered list
    let expense = expenses.find(e => e.id === obligationId);
    
    if (expense) {
      // Expense is visible - scroll to it
      const expenseRow = document.getElementById(`expense-row-${obligationId}`);
      if (expenseRow) {
        expenseRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        expenseRow.classList.add('bg-amber-100');
        setTimeout(() => {
          expenseRow.classList.remove('bg-amber-100');
        }, 2000);
      }
      handleEditExpense(expense);
    } else {
      // Expense not in current view - fetch it and adjust filter
      try {
        const res = await api.get(`/expenses/${obligationId}`);
        expense = res.data;
        
        if (expense && expense.date) {
          // Adjust period to include this expense's date
          const expenseDate = new Date(expense.date);
          const newStart = startOfMonth(expenseDate);
          const newEnd = endOfMonth(expenseDate);
          
          // Update the period filter
          setPeriodPreset('custom');
          setPeriodStart(newStart);
          setPeriodEnd(newEnd);
          
          // Show toast and open edit after a brief delay for data to reload
          toast.info(`Switched to ${format(expenseDate, 'MMMM yyyy')} to show expense`);
          
          // Store expense to edit after the data refreshes
          setTimeout(() => {
            handleEditExpense(expense);
          }, 500);
        }
      } catch (err) {
        toast.error('Could not load expense details');
      }
    }
  };

  // Quick approve - confirm expense as-is
  const handleQuickApprove = async (expense) => {
    setApproving(expense.id);
    try {
      await api.put(`/expenses/${expense.id}/confirm`, {
        vendor: expense.vendor,
        date: expense.date,
        amount: expense.amount,
        category: expense.category,
        is_recurring: expense.is_recurring,
        recurrence_frequency: expense.recurrence_frequency,
        recurrence_end_date: expense.recurrence_end_date
      });
      toast.success('Expense approved');
      fetchExpenses();
    } catch (err) {
      console.error('Error approving expense:', err);
      toast.error('Failed to approve expense');
    } finally {
      setApproving(null);
    }
  };

  // Delete expense
  const handleDeleteExpense = async () => {
    if (!deleteExpense) return;
    try {
      await api.delete(`/expenses/${deleteExpense.id}`);
      toast.success('Expense deleted');
      setDeleteExpense(null);
      fetchExpenses();
      fetchUpcomingExpenses();
    } catch (err) {
      console.error('Error deleting expense:', err);
      toast.error('Failed to delete expense');
    }
  };

  const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });

  const getCategoryBadge = (category, isPersonal) => {
    if (isPersonal) {
      return <Badge className="bg-violet-100 text-violet-800 border-violet-200">Personal</Badge>;
    }
    const styles = {
      cogs: 'badge-cogs',
      labor: 'badge-labour',
      operating: 'badge-operating'
    };
    const labels = {
      cogs: 'Cost of Goods',
      labor: 'Labour',
      operating: 'Operating'
    };
    return (
      <Badge className={styles[category] || 'badge-operating'}>
        {labels[category] || category}
      </Badge>
    );
  };

  // Calculate totals (exclude personal from business totals)
  const businessExpenses = expenses.filter(e => !e.is_personal);
  const personalExpenses = expenses.filter(e => e.is_personal);
  const totalExpenses = businessExpenses.filter(e => e.confirmed).reduce((sum, e) => sum + (e.amount || 0), 0);
  const pendingCount = businessExpenses.filter(e => !e.confirmed).length;
  const confirmedCount = businessExpenses.filter(e => e.confirmed).length;
  const unpaidCount = expenses.filter(e => e.confirmed && !e.is_paid).length;
  const paidCount = expenses.filter(e => e.confirmed && e.is_paid).length;

  // Filter expenses based on payment status
  const filteredExpenses = expenses.filter(e => {
    if (paymentFilter === 'paid') return e.is_paid;
    if (paymentFilter === 'unpaid') return !e.is_paid;
    if (paymentFilter === 'personal') return e.is_personal;
    return true;
  });

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Expenses</h1>
          <p className="text-muted-foreground">Track your costs — imports live in the Imports hub.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <PeriodSelector
            startDate={periodStart}
            endDate={periodEnd}
            preset={periodPreset}
            onPresetChange={setPeriodPreset}
            onChange={(s, e) => { setPeriodStart(s); setPeriodEnd(e); }}
          />
          {/* Desktop button — imports now handled centrally */}
          <Button
            variant="outline"
            onClick={() => navigate('/imports?tab=receipts')}
            disabled={!currentLocation}
            data-testid="open-imports-btn"
            className="hidden sm:inline-flex"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import receipts
          </Button>
          <Button 
            onClick={() => navigate('/smart-invoice')}
            disabled={!currentLocation}
            className="hidden sm:inline-flex bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
            data-testid="smart-invoice-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Smart Invoice
          </Button>
          {/* Mobile icon-only buttons */}
          <Button 
            variant="outline"
            size="icon"
            onClick={() => navigate('/imports?tab=receipts')}
            disabled={!currentLocation}
            className="sm:hidden"
            title="Import receipts"
            data-testid="open-imports-btn-mobile"
          >
            <Upload className="w-4 h-4" />
          </Button>
          <Button 
            size="icon"
            onClick={() => navigate('/smart-invoice')}
            disabled={!currentLocation}
            className="sm:hidden bg-gradient-to-r from-primary to-purple-600"
            title="Smart Invoice"
          >
            <Sparkles className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="expenses" className="space-y-6" data-testid="expenses-tabs">
        <TabsList>
          <TabsTrigger value="expenses" data-testid="expenses-tab">Expenses</TabsTrigger>
          <TabsTrigger value="fixed-costs" data-testid="fixed-costs-tab">Fixed Costs</TabsTrigger>
        </TabsList>

        <TabsContent value="fixed-costs">
          <FixedCostsTab />
        </TabsContent>

        <TabsContent value="expenses" className="space-y-6">

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Expenses
            </CardTitle>
            <Receipt className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num text-loss" data-testid="total-expenses">
              {formatCurrency(totalExpenses)}
            </div>
            <p className="text-sm text-muted-foreground">Confirmed expenses</p>
          </CardContent>
        </Card>

        <Card className={`card-hover ${obligations?.overdue_count > 0 ? 'border-destructive bg-destructive/5' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unpaid
            </CardTitle>
            <Clock className={`w-4 h-4 ${obligations?.overdue_count > 0 ? 'text-destructive' : 'text-amber-500'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono-num" data-testid="unpaid-total">
              {formatCurrency(obligations?.total_unpaid || 0)}
            </div>
            <div className="flex flex-col gap-0.5 mt-1">
              {obligations?.overdue_count > 0 && (
                <p className="text-xs text-destructive font-medium">
                  {obligations.overdue_count} overdue ({formatCurrency(obligations.overdue_amount)})
                </p>
              )}
              {obligations?.due_this_week_count > 0 && (
                <p className="text-xs text-amber-600">
                  {obligations.due_this_week_count} due this week
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Paid
            </CardTitle>
            <CheckCircle2 className="w-4 h-4 text-profit" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-profit">{paidCount}</div>
            <p className="text-sm text-muted-foreground">Expenses settled</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Review
            </CardTitle>
            <AlertCircle className="w-4 h-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{pendingCount}</div>
            <p className="text-sm text-muted-foreground">Need confirmation</p>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Obligations */}
      {obligations?.upcoming_obligations?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <CardTitle className="text-base">Upcoming Payments</CardTitle>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Click to view and edit</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {obligations.upcoming_obligations.map((obl) => (
                <Badge 
                  key={obl.id} 
                  variant={obl.is_overdue ? "destructive" : "outline"}
                  className="py-1.5 px-3 cursor-pointer hover:opacity-80 hover:shadow-sm transition-all"
                  onClick={() => handleUpcomingPaymentClick(obl.id)}
                  data-testid={`upcoming-payment-${obl.id}`}
                >
                  <span className="font-medium">{obl.vendor}</span>
                  <span className="mx-2">•</span>
                  <span className="mono-num">{formatCurrency(obl.amount)}</span>
                  {obl.due_date && (
                    <>
                      <span className="mx-2">•</span>
                      <span className={obl.is_overdue ? 'text-destructive-foreground' : ''}>
                        {obl.is_overdue ? 'Overdue' : `Due ${formatDate(obl.due_date)}`}
                      </span>
                    </>
                  )}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bot Uploads - Pending Review */}
      {botUploads.length > 0 && (
        <Collapsible open={botUploadsOpen} onOpenChange={setBotUploadsOpen}>
          <Card className="border-purple-200 bg-purple-50/30">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <div>
                      <CardTitle className="text-base">Bot Uploads</CardTitle>
                      <CardDescription>
                        {botUploads.length} receipt{botUploads.length !== 1 ? 's' : ''} from Telegram need review
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                      {botUploads.length} pending
                    </Badge>
                    {botUploadsOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {botUploads.map((upload) => (
                    <div 
                      key={upload.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border"
                      data-testid={`bot-upload-${upload.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded flex items-center justify-center">
                          <FileImage className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{upload.filename || 'Receipt'}</p>
                          <p className="text-xs text-muted-foreground">
                            {upload.partial_ocr_data?.supplier_name && (
                              <span>Detected: {upload.partial_ocr_data.supplier_name} • </span>
                            )}
                            {upload.partial_ocr_data?.total && (
                              <span>{formatCurrency(upload.partial_ocr_data.total)} • </span>
                            )}
                            {new Date(upload.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {upload.file_url && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => window.open(`${api.defaults.baseURL}${upload.file_url}`, '_blank')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleProcessBotUpload(upload)}
                          data-testid={`process-upload-${upload.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Process
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => rejectBotUpload(upload.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Upcoming Recurring Expenses */}
      {upcomingExpenses.length > 0 && (
        <Collapsible open={upcomingOpen} onOpenChange={setUpcomingOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">Upcoming Expenses</CardTitle>
                      <CardDescription>
                        {upcomingExpenses.length} recurring expense{upcomingExpenses.length !== 1 ? 's' : ''} scheduled
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="font-mono">
                      {formatCurrency(upcomingExpenses.reduce((sum, e) => sum + e.amount, 0))}
                    </Badge>
                    {upcomingOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {upcomingExpenses.map((expense, idx) => (
                        <TableRow key={`${expense.source_expense_id}-${idx}`} className="bg-muted/30">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-muted-foreground" />
                              {new Date(expense.projected_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{expense.vendor}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="gap-1">
                              <RefreshCw className="w-3 h-3" />
                              {expense.recurrence_frequency}
                            </Badge>
                          </TableCell>
                          <TableCell>{getCategoryBadge(expense.category, expense.is_personal)}</TableCell>
                          <TableCell className="text-right font-mono text-loss">
                            {formatCurrency(expense.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Expense Entries</CardTitle>
              <CardDescription>All recorded expenses from receipts and manual entries</CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger className="w-[160px]" data-testid="payment-filter">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({expenses.length})</SelectItem>
                  <SelectItem value="unpaid">Unpaid ({unpaidCount})</SelectItem>
                  <SelectItem value="paid">Paid ({paidCount})</SelectItem>
                  {personalExpenses.length > 0 && (
                    <SelectItem value="personal">Personal ({personalExpenses.length})</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredExpenses.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">
                {expenses.length === 0 ? 'No expenses recorded' : 'No matching expenses'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {expenses.length === 0 
                  ? 'Import a receipt from the Imports hub to automatically extract expense data'
                  : 'Try adjusting your filter'}
              </p>
              {expenses.length === 0 && (
                <Button onClick={() => navigate('/imports?tab=receipts')} data-testid="empty-open-imports-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Go to Imports
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredExpenses.map((expense) => (
                  <div 
                    key={expense.id}
                    id={`expense-row-${expense.id}`}
                    className="border rounded-lg p-4 space-y-3 transition-colors duration-500"
                  >
                    {/* Top row: Status badge + Amount */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        {expense.confirmed ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <Check className="w-3 h-3 mr-1" /> Approved
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            Pending
                          </Badge>
                        )}
                        {expense.is_paid ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Paid</Badge>
                        ) : (
                          <Badge variant="outline" className="text-muted-foreground">Unpaid</Badge>
                        )}
                      </div>
                      <span className="text-lg font-bold">{formatCurrency(expense.amount)}</span>
                    </div>
                    
                    {/* Vendor and Category */}
                    <div>
                      <p className="font-medium">{expense.vendor || 'Unknown Vendor'}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <span>{format(new Date(expense.date), 'dd MMM yyyy')}</span>
                        <span>•</span>
                        <span className="capitalize">{expense.category}</span>
                      </p>
                    </div>
                    
                    {/* Description if exists */}
                    {expense.description && (
                      <p className="text-sm text-muted-foreground truncate">{expense.description}</p>
                    )}
                    
                    {/* Actions row */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex gap-1">
                        {!expense.confirmed && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleQuickApprove(expense)}
                            disabled={approving === expense.id}
                            className="text-green-600"
                          >
                            {approving === expense.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><CheckCheck className="w-4 h-4 mr-1" /> Approve</>
                            )}
                          </Button>
                        )}
                        {!expense.is_paid && expense.confirmed && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleTogglePayment(expense)}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {expense.receipt_file_id && (
                            <DropdownMenuItem onClick={() => handleViewReceipt(expense.id)}>
                              <Eye className="w-4 h-4 mr-2" /> View Receipt
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleEditExpense(expense)}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setDeleteExpense(expense)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredExpenses.map((expense) => (
                    <TableRow 
                      key={expense.id} 
                      id={`expense-row-${expense.id}`}
                      className="table-row-hover transition-colors duration-500"
                    >
                      <TableCell>
                        {expense.confirmed ? (
                          <CheckCircle2 className="w-5 h-5 text-profit" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                        )}
                      </TableCell>
                      <TableCell>
                        {editingExpense === expense.id ? (
                          <Input
                            type="date"
                            value={editForm.date}
                            onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                            className="w-32"
                          />
                        ) : (
                          <span className="font-medium">{formatDate(expense.date)}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingExpense === expense.id ? (
                          <Input
                            value={editForm.vendor}
                            onChange={(e) => setEditForm({...editForm, vendor: e.target.value})}
                            className="w-40"
                          />
                        ) : (
                          expense.vendor
                        )}
                      </TableCell>
                      <TableCell>
                        {editingExpense === expense.id ? (
                          <Select
                            value={editForm.category}
                            onValueChange={(val) => setEditForm({...editForm, category: val})}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cogs">Cost of Goods</SelectItem>
                              <SelectItem value="labor">Labour</SelectItem>
                              <SelectItem value="operating">Operating</SelectItem>
                              <SelectItem value="personal">Personal</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          getCategoryBadge(expense.category, expense.is_personal)
                        )}
                      </TableCell>
                      <TableCell>
                        {editingExpense === expense.id ? (
                          <div className="space-y-1">
                            <Input
                              type="date"
                              value={editForm.due_date || ''}
                              onChange={(e) => setEditForm({...editForm, due_date: e.target.value})}
                              className="w-32 h-7 text-xs"
                              placeholder="Due date"
                            />
                            <span className="text-xs text-muted-foreground">Due date</span>
                          </div>
                        ) : expense.confirmed ? (
                          <div className="flex flex-col gap-1">
                            {/* Show payment progress for partial payments */}
                            {(expense.payments?.length > 0 || expense.amount_paid > 0) && !expense.is_paid ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-amber-500" 
                                      style={{ width: `${((expense.amount_paid || 0) / expense.amount) * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-mono">{Math.round(((expense.amount_paid || 0) / expense.amount) * 100)}%</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-xs"
                                    onClick={() => openPaymentHistory(expense)}
                                    title="View payment history"
                                  >
                                    £{(expense.amount_paid || 0).toFixed(0)} paid
                                  </Button>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    className="h-6 text-xs"
                                    onClick={() => openPartialPayment(expense)}
                                    title="Make payment"
                                  >
                                    Pay
                                  </Button>
                                </div>
                              </div>
                            ) : expense.is_paid ? (
                              <Button
                                variant="default"
                                size="sm"
                                className="h-7 text-xs bg-profit hover:bg-profit/90"
                                onClick={() => expense.payments?.length > 0 ? openPaymentHistory(expense) : handleTogglePayment(expense)}
                                data-testid={`toggle-payment-${expense.id}`}
                              >
                                <CheckCheck className="w-3 h-3 mr-1" />
                                Paid
                              </Button>
                            ) : (
                              <div className="flex gap-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => handleTogglePayment(expense)}
                                  data-testid={`toggle-payment-${expense.id}`}
                                  title="Mark as fully paid"
                                >
                                  <Clock className="w-3 h-3 mr-1" />
                                  Unpaid
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={() => openPartialPayment(expense)}
                                  title="Make partial payment"
                                >
                                  Pay
                                </Button>
                              </div>
                            )}
                            {expense.due_date && !expense.is_paid && (
                              <span className={`text-xs ${new Date(expense.due_date) < new Date() ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                Due: {formatDate(expense.due_date)}
                              </span>
                            )}
                            {expense.is_recurring && (
                              <Badge variant="secondary" className="gap-1 w-fit text-xs">
                                <RefreshCw className="w-2 h-2" />
                                {expense.recurrence_frequency}
                              </Badge>
                            )}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingExpense === expense.id ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.amount}
                            onChange={(e) => setEditForm({...editForm, amount: parseFloat(e.target.value)})}
                            className="w-24 text-right"
                          />
                        ) : (
                          <span className="mono-num font-medium text-loss">
                            {formatCurrency(expense.amount)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingExpense === expense.id ? (
                          <div className="flex gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => handleSaveEdit(expense.id)}
                              data-testid={`save-expense-${expense.id}`}
                            >
                              <Check className="w-4 h-4 text-profit" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => setEditingExpense(null)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            {/* Quick Approve - only show for unconfirmed expenses */}
                            {!expense.confirmed && (
                              <Button 
                                size="icon" 
                                variant="ghost"
                                onClick={() => handleQuickApprove(expense)}
                                disabled={approving === expense.id}
                                title="Approve expense"
                                data-testid={`approve-expense-${expense.id}`}
                                className="text-profit hover:text-profit hover:bg-profit/10"
                              >
                                {approving === expense.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <CheckCheck className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {expense.receipt_file_id && (
                              <Button 
                                size="icon" 
                                variant="ghost"
                                onClick={() => handleViewReceipt(expense.id)}
                                title="View receipt"
                                data-testid={`view-receipt-${expense.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => handleEditExpense(expense)}
                              title="Edit expense"
                              data-testid={`edit-expense-${expense.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => setDeleteExpense(expense)}
                              title="Delete expense"
                              data-testid={`delete-expense-${expense.id}`}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteExpense} onOpenChange={() => setDeleteExpense(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense from {deleteExpense?.vendor || 'Unknown Vendor'} 
              for {formatCurrency(deleteExpense?.amount)}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteExpense}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pending Confirmation Dialog */}
      <Dialog open={!!pendingExpense} onOpenChange={() => { setPendingExpense(null); setReceiptImage(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Confirm Expense
            </DialogTitle>
            <DialogDescription>
              Review and edit the extracted data from your receipt
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Receipt Image Preview */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileImage className="w-4 h-4" />
                Receipt Image
              </Label>
              <div className="border rounded-lg overflow-hidden bg-muted/30 min-h-[300px] flex items-center justify-center">
                {loadingImage ? (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-8 h-8 animate-spin" />
                    <span className="text-sm">Loading image...</span>
                  </div>
                ) : receiptImage ? (
                  <img 
                    src={receiptImage} 
                    alt="Receipt" 
                    className="max-w-full max-h-[400px] object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground p-4">
                    <FileImage className="w-8 h-8" />
                    <span className="text-sm">Image preview not available</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{pendingExpense?.filename}</p>
            </div>
            
            {/* Extracted Data Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Vendor / Supplier</Label>
                <Input
                  value={pendingExpense?.vendor || ''}
                  onChange={(e) => setPendingExpense({...pendingExpense, vendor: e.target.value})}
                  placeholder="e.g., Costco, Metro"
                  data-testid="confirm-vendor-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={pendingExpense?.date || ''}
                  onChange={(e) => setPendingExpense({...pendingExpense, date: e.target.value})}
                  data-testid="confirm-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Total Amount (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={pendingExpense?.amount || ''}
                  onChange={(e) => setPendingExpense({...pendingExpense, amount: parseFloat(e.target.value)})}
                  placeholder="0.00"
                  data-testid="confirm-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={pendingExpense?.suggested_category || 'operating'}
                  onValueChange={(val) => setPendingExpense({...pendingExpense, suggested_category: val})}
                >
                  <SelectTrigger data-testid="confirm-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cogs">Cost of Goods Sold (ingredients, supplies)</SelectItem>
                    <SelectItem value="labor">Labour (wages, benefits)</SelectItem>
                    <SelectItem value="operating">Operating Expenses (utilities, rent, services)</SelectItem>
                    <SelectItem value="personal">Personal (not included in P&L)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Recurring Expense Options */}
              <div className="space-y-3 pt-2 border-t">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={pendingExpense?.is_recurring || false}
                    onChange={(e) => setPendingExpense({...pendingExpense, is_recurring: e.target.checked})}
                    className="rounded"
                    data-testid="confirm-recurring-checkbox"
                  />
                  <span className="text-sm font-medium">This is a recurring expense</span>
                </label>
                
                {pendingExpense?.is_recurring && (
                  <div className="grid grid-cols-2 gap-3 pl-6">
                    <div className="space-y-1">
                      <Label className="text-xs">Frequency</Label>
                      <Select
                        value={pendingExpense?.recurrence_frequency || ''}
                        onValueChange={(val) => setPendingExpense({...pendingExpense, recurrence_frequency: val})}
                      >
                        <SelectTrigger className="h-9" data-testid="confirm-frequency-select">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">End Date (optional)</Label>
                      <Input
                        type="date"
                        value={pendingExpense?.recurrence_end_date || ''}
                        onChange={(e) => setPendingExpense({...pendingExpense, recurrence_end_date: e.target.value})}
                        className="h-9"
                        data-testid="confirm-end-date-input"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Payment Due Date */}
              <div className="space-y-2 pt-2 border-t">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Payment Due Date (optional)
                </Label>
                <Input
                  type="date"
                  value={pendingExpense?.due_date || ''}
                  onChange={(e) => setPendingExpense({...pendingExpense, due_date: e.target.value})}
                  data-testid="confirm-due-date-input"
                />
                <p className="text-xs text-muted-foreground">Track when this expense needs to be paid</p>
              </div>
              
              {/* Line Items Preview */}
              {pendingExpense?.line_items && pendingExpense.line_items.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm">Detected Line Items</Label>
                  <div className="border rounded-md p-3 bg-muted/30 max-h-[150px] overflow-y-auto">
                    <div className="space-y-1 text-sm">
                      {pendingExpense.line_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-muted-foreground truncate flex-1 mr-2">
                            {item.quantity && item.quantity > 1 ? `${item.quantity}x ` : ''}{item.item}
                          </span>
                          <span className="font-mono">
                            {item.amount ? `£${item.amount.toFixed(2)}` : '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPendingExpense(null); setReceiptImage(null); }}>
              Cancel
            </Button>
            <Button onClick={handleConfirmExpense} data-testid="confirm-expense-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Confirm Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Result Dialog */}
      <Dialog open={!!payrollResult} onOpenChange={() => setPayrollResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-profit" />
              Payroll Imported
            </DialogTitle>
            <DialogDescription>
              Period: {payrollResult?.period_start} to {payrollResult?.period_end}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Employees processed</span>
              <span className="font-semibold">{payrollResult?.employees_processed || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">New employees added</span>
              <span className="font-semibold">{payrollResult?.employees_created || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Labour expenses created</span>
              <span className="font-semibold">{payrollResult?.expenses_created || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-muted-foreground">Labour expenses updated</span>
              <span className="font-semibold">{payrollResult?.expenses_updated || 0}</span>
            </div>
            {payrollResult?.demo_entries_removed > 0 && (
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-muted-foreground">Demo data replaced</span>
                <span className="font-semibold text-amber-600">{payrollResult?.demo_entries_removed}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-2 bg-muted/50 -mx-6 px-6 rounded">
              <span className="font-semibold">Total Labour Cost</span>
              <span className="font-bold mono-num text-loss">
                {formatCurrency(payrollResult?.total_labor_cost)}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setPayrollResult(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Receipt Dialog */}
      <Dialog open={!!viewingReceipt} onOpenChange={() => setViewingReceipt(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileImage className="w-5 h-5" />
              Receipt
            </DialogTitle>
            <DialogDescription>
              {viewingReceipt?.filename}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="border rounded-lg overflow-hidden bg-muted/30 min-h-[300px] flex items-center justify-center">
              {viewingReceipt?.data_url ? (
                viewingReceipt.mime_type === 'application/pdf' ? (
                  <div className="flex flex-col items-center gap-4 p-8">
                    <FileImage className="w-16 h-16 text-muted-foreground" />
                    <p className="text-muted-foreground">PDF preview not available</p>
                    <a 
                      href={viewingReceipt.data_url} 
                      download={viewingReceipt.filename}
                      className="text-primary hover:underline"
                    >
                      Download PDF
                    </a>
                  </div>
                ) : (
                  <img 
                    src={viewingReceipt.data_url} 
                    alt="Receipt" 
                    className="max-w-full max-h-[500px] object-contain"
                  />
                )
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <FileImage className="w-8 h-8" />
                  <span className="text-sm">Receipt image not available</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setViewingReceipt(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Dialog */}
      <Dialog open={!!partialPaymentDialog} onOpenChange={() => setPartialPaymentDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Make Payment</DialogTitle>
            <DialogDescription>
              Record a payment for {partialPaymentDialog?.vendor}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-md">
              <div className="flex justify-between text-sm">
                <span>Total Amount:</span>
                <span className="font-mono font-medium">{formatCurrency(partialPaymentDialog?.amount || 0)}</span>
              </div>
              <div className="flex justify-between text-sm text-green-600">
                <span>Already Paid:</span>
                <span className="font-mono">{formatCurrency(partialPaymentDialog?.amount_paid || 0)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium border-t pt-2 mt-2">
                <span>Remaining:</span>
                <span className="font-mono text-loss">
                  {formatCurrency((partialPaymentDialog?.amount || 0) - (partialPaymentDialog?.amount_paid || 0))}
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={(partialPaymentDialog?.amount || 0) - (partialPaymentDialog?.amount_paid || 0)}
                value={partialPaymentForm.amount}
                onChange={(e) => setPartialPaymentForm({...partialPaymentForm, amount: e.target.value})}
                data-testid="partial-payment-amount"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={partialPaymentForm.payment_date}
                onChange={(e) => setPartialPaymentForm({...partialPaymentForm, payment_date: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={partialPaymentForm.notes}
                onChange={(e) => setPartialPaymentForm({...partialPaymentForm, notes: e.target.value})}
                placeholder="e.g., Bank transfer ref"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartialPaymentDialog(null)}>Cancel</Button>
            <Button onClick={handlePartialPayment} data-testid="confirm-partial-payment">
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={!!paymentHistoryDialog} onOpenChange={() => setPaymentHistoryDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payment History</DialogTitle>
            <DialogDescription>
              {paymentHistoryDialog?.vendor} - {formatCurrency(paymentHistoryDialog?.amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {paymentHistoryDialog?.payments?.length > 0 ? (
              <div className="space-y-2">
                {paymentHistoryDialog.payments.map((payment, idx) => (
                  <div key={payment.id || idx} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                    <div>
                      <div className="font-mono font-medium text-green-700">
                        £{payment.amount.toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(payment.payment_date)}
                        {payment.notes && ` • ${payment.notes}`}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteExpensePayment(paymentHistoryDialog.id, payment.id)}
                      title="Delete payment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <div className="pt-3 border-t mt-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Total Paid:</span>
                    <span className="font-mono text-green-600">
                      £{(paymentHistoryDialog.amount_paid || paymentHistoryDialog.payments.reduce((sum, p) => sum + p.amount, 0)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm font-medium">
                    <span>Remaining:</span>
                    <span className={`font-mono ${paymentHistoryDialog.is_paid ? 'text-green-600' : 'text-loss'}`}>
                      £{(paymentHistoryDialog.amount - (paymentHistoryDialog.amount_paid || paymentHistoryDialog.payments.reduce((sum, p) => sum + p.amount, 0))).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No payments recorded yet</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPaymentHistoryDialog(null)}>Close</Button>
            {!paymentHistoryDialog?.is_paid && (
              <Button onClick={() => {
                setPaymentHistoryDialog(null);
                openPartialPayment(paymentHistoryDialog);
              }}>
                Make Payment
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Bot Upload Dialog */}
      <Dialog open={!!processingUpload} onOpenChange={(open) => !open && setProcessingUpload(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Process Receipt</DialogTitle>
            <DialogDescription>
              Complete the details to create an expense from this Telegram upload.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Input
                value={processForm.vendor}
                onChange={(e) => setProcessForm(prev => ({ ...prev, vendor: e.target.value }))}
                placeholder="e.g., Funnybones"
                data-testid="process-vendor-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={processForm.amount}
                  onChange={(e) => setProcessForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  data-testid="process-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={processForm.date}
                  onChange={(e) => setProcessForm(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="process-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={processForm.category}
                onValueChange={(val) => setProcessForm(prev => ({ ...prev, category: val }))}
              >
                <SelectTrigger data-testid="process-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cogs">Cost of Goods</SelectItem>
                  <SelectItem value="labor">Labor</SelectItem>
                  <SelectItem value="utilities">Utilities</SelectItem>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {processingUpload?.file_url && (
              <div className="pt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => window.open(`${api.defaults.baseURL}${processingUpload.file_url}`, '_blank')}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Receipt
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProcessingUpload(null)}>
              Cancel
            </Button>
            <Button onClick={submitProcessBotUpload} data-testid="submit-process-btn">
              Create Expense
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpensesPage;
